// Create a new file: OrdersPopup.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { getCookies } from "../../hooks/useCookies";
import { DateFormatter } from "../../utilityFunctions/utilsFunctions";
import Loader from "../../components/Loader/Loader";
import styles from "./OrdersPopup.module.scss"; // Create this CSS file
import { SERVER_BASE_URL } from "../../redux/apiConstants";

import ViewOrderDetailsPopup from "./ViewOrderDetailsPopup";
import { Pagination } from "react-bootstrap";
import EntityProfileLink from "../../components/ProfileLinks/EntityProfileLink";

const OrdersPopup = ({ userId, type = "user", handleClose }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const token = `Bearer ${getCookies("Token")}`;
  const dateFormat = getCookies("dateFormat");
  const [pageData, setPageData] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalResults: 0,
  });

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pageData.totalPages) return;
    setPageData((prev) => ({ ...prev, page: newPage }));
  };

  useEffect(() => {
    if (userId) {
      fetchOrders(pageData.page, pageData.limit);
    }
  }, [userId, pageData.page]);

  const fetchOrders = async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${SERVER_BASE_URL}/orders/${type}/${userId}?page=${page}&limit=${limit}`,
        { headers: { Authorization: token } }
      );

      if (response.status === 200) {
        const result = response.data.data.result;
        setOrders(result.results || []);
        setPageData((prev) => ({
          ...prev,
          page: result.page,
          totalPages: result.totalPages,
          totalResults: result.totalResults,
        }));
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };
  function getOrderAmount(order) {
    if (order.totalAmount != null) {
      return order.totalAmount;
    }
    if (order.amount != null) {
      return order.amount;
    }
    return 0;
  }
  return (
    <div className={styles.popupOverlay}>
      <div className={styles.popupContent}>
        <div className={styles.popupHeader}>
          <h3>{type === "user" ? "User Orders" : "Cafe Orders"}</h3>
          <button className={styles.closeButton} onClick={handleClose}>
            ×
          </button>
        </div>

        <div className={styles.popupBody}>
          {loading ? (
            <div className={styles.loaderContainer}>
              <Loader />
            </div>
          ) : orders.length > 0 ? (
            <div className={styles.ordersTable}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    {type === "user" ? <th>Cafe</th> : <th>User</th>}
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id}>
                      <td 
                        onClick={() => {
                          setSelectedOrderId(order.order_id || order.id);
                          setShowDetailsPopup(true);
                        }}
                        style={{cursor: 'pointer', color: '#39979d', textDecoration: 'underline'}}
                      >
                        #{order.order_id || order.id}
                      </td>
                      <td>
                        {DateFormatter(
                          order.createdAt,
                          dateFormat || "MonthDayYear"
                        )}
                      </td>
                      <td>${getOrderAmount(order)}</td>
                      <td>
                        <span
                          className={`${styles.status} ${styles[order.status]}`}
                        >
                          {order.orderStatus}
                        </span>
                      </td>
                      {type === "user" ? (
                        <td>
                          <EntityProfileLink type="cafe" entity={order.cafe} />
                        </td>
                      ) : (
                        <td>
                          <EntityProfileLink type="user" entity={order.user} />
                        </td>
                      )}
                    </tr>
                  ))}
                  </tbody>
              </table>
              {pageData.totalPages > 1 && (
                <div className={`d-flex justify-content-between align-items-center mt-2 px-2 ${styles.paginationWrapper}`}>
                  <small style={{ color: "#718096" }}>
                    Page {pageData.page} of {pageData.totalPages} ({pageData.totalResults} orders)
                  </small>
                  <Pagination size="sm" className="mb-0">
                    <Pagination.Prev
                      disabled={pageData.page <= 1}
                      onClick={() => handlePageChange(pageData.page - 1)}
                    />
                    {Array.from({ length: pageData.totalPages }, (_, i) => i + 1)
                      .filter((p) => {
                        const current = pageData.page;
                        return p === 1 || p === pageData.totalPages || Math.abs(p - current) <= 1;
                      })
                      .reduce((acc, p, idx, arr) => {
                        if (idx > 0 && p - arr[idx - 1] > 1) {
                          acc.push("ellipsis-" + p);
                        }
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p) =>
                        typeof p === "string" ? (
                          <Pagination.Ellipsis key={p} disabled />
                        ) : (
                          <Pagination.Item
                            key={p}
                            active={p === pageData.page}
                            onClick={() => handlePageChange(p)}
                          >
                            {p}
                          </Pagination.Item>
                        )
                      )}
                    <Pagination.Next
                      disabled={pageData.page >= pageData.totalPages}
                      onClick={() => handlePageChange(pageData.page + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </div>
          ) : (
            <div className={styles.noOrders}>
              <p>No orders found for this {type}.</p>
            </div>
          )}
        </div>

        <div className={styles.popupFooter}>
          <button className={styles.closeBtn} onClick={handleClose}>
            Close
          </button>
        </div>
      </div>
      {showDetailsPopup && (
        <ViewOrderDetailsPopup 
          orderId={selectedOrderId} 
          handleClose={() => setShowDetailsPopup(false)} 
        />
      )}
    </div>
  );
};

export default OrdersPopup;
